import Layout from "@/components/layout/Layout";
import ProductChrome from "@/components/product/ProductChrome";
import { Check } from "lucide-react";

export const authInput = (light) => light
    ? "w-full rounded-xl border border-zinc-300 bg-white px-4 py-3 text-zinc-950 placeholder:text-zinc-400 focus:border-zinc-950 focus:outline-none"
    : "w-full rounded-xl border border-white/15 bg-brand-ink px-4 py-3 text-white placeholder:text-zinc-500 focus:border-brand-green focus:outline-none";

const PLANS = {
    kiosk: ["Starter", 10, 70], storefront: ["Storefront", 39, 299],
    department: ["Pro Shop", 99, 790], standalone: ["Virtual Brick & Mortar", 299, 1990],
};

/** Which plan the visitor tapped on the pitch page, read straight off ?next=. */
export const planFrom = (next = "") => {
    const id = /tier=([a-z]+)/.exec(next)?.[1];
    if (!id || !PLANS[id]) return null;
    const [name, price, year] = PLANS[id];
    return { id, name, price, year, cycle: /cycle=yearly/.test(next) ? "yearly" : "monthly" };
};

/**
 * Signing up is part of the sale, so when a visitor arrives from the white-label page they keep
 * that page's header, footer and colors — no dark mall chrome, no "you are on 13thav" bar, and
 * a running list of exactly what happens next.
 */
export default function AuthFrame({ light, title, sub, plan, next, children, footer }) {
    const steps = [
        ["Create your account.", "Your name, your email and a password. It takes ten seconds."],
        [plan ? `Pay for the ${plan.name} plan.` : "Pick your plan.",
            !plan ? "There are four plans. Your first month is 50% off, and you can cancel any time."
                : plan.cycle === "yearly"
                    ? `You pay $${plan.year.toLocaleString()} a year instead of $${(plan.price * 12).toLocaleString()} paid monthly, and you keep the difference.`
                    : `You pay $${plan.price} a month. Your first month is $${(plan.price / 2).toFixed(2)}, which is half off.`],
        ["Wizard Mode opens by itself.", "Answer nine short questions, or press Go Live Now and your shop opens in about a minute."],
    ];

    const inner = light ? (
        <div className="mx-auto grid max-w-5xl gap-x-16 gap-y-10 px-5 py-14 sm:px-8 lg:grid-cols-[1fr_.8fr]">
            <div className="max-w-md" data-testid="auth-card">
                <p className="font-mono text-[11px] font-bold uppercase tracking-[0.24em] text-zinc-500">
                    {plan ? `Step 1 of 3 · ${plan.name}` : "Step 1 of 3"}
                </p>
                <h1 className="mt-3 font-display text-3xl font-black tracking-[-0.02em] text-zinc-950">{title}</h1>
                <p className="mt-1.5 text-[15px] font-light text-zinc-600">{sub}</p>
                {children}
                {footer}
            </div>
            <aside className="self-start rounded-2xl border border-zinc-200 bg-zinc-50 p-6" data-testid="auth-steps">
                <p className="font-display text-[15px] font-extrabold tracking-tight text-zinc-950">
                    What happens next
                </p>
                <ol className="mt-4 space-y-4">
                    {steps.map(([head, body], i) => (
                        <li key={head} className="flex gap-3">
                            <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-zinc-950 font-mono text-[11px] font-bold text-white">
                                {i + 1}
                            </span>
                            <span>
                                <span className="block text-[14px] font-bold text-zinc-950">{head}</span>
                                <span className="block text-[13px] font-light leading-snug text-zinc-600">{body}</span>
                            </span>
                        </li>
                    ))}
                </ol>
                <ul className="mt-6 space-y-2 border-t border-zinc-200 pt-4">
                    {["50% off your first month", "1% of sales, nothing hidden",
                        "Your own Stripe — we never hold your money", "Cancel any time"].map((b) => (
                            <li key={b} className="flex gap-2 text-[13px] font-light text-zinc-600">
                                <Check size={13} className="mt-0.5 shrink-0 text-[#1E40AF]" /> {b}
                            </li>
                        ))}
                </ul>
            </aside>
        </div>
    ) : (
        <div className="mx-auto flex min-h-[70vh] max-w-md flex-col justify-center px-4 py-12">
            <h1 className="font-display text-3xl font-black tracking-tight">{title}</h1>
            <p className="mt-1 text-zinc-400">{sub}</p>
            {children}
            {footer}
        </div>
    );

    return light ? <ProductChrome at="/" loginNext={next}>{inner}</ProductChrome> : <Layout hideTicker>{inner}</Layout>;
}
